import { useState } from "react";
import { Button, Loading, Modal, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useRemoveNetworkMutation } from "@/redux/apis/network/networkApi";
import { NetworkItem } from "@/redux/slices/network/types";
import { addToast } from "@/utils";
import { CannotDisconnectModal } from "./CannotDisconnectModal";

interface DisconnectRelationshipModalProps {
  pendingDisconnectRelationship: NetworkItem | undefined;
  setPendingDisconnectRelationship: (item: NetworkItem | undefined) => void;
}

export const DisconnectRelationshipModal = ({
  pendingDisconnectRelationship,
  setPendingDisconnectRelationship,
}: DisconnectRelationshipModalProps) => {
  const [removeNetwork, { isLoading }] = useRemoveNetworkMutation();
  const [showCannotDisconnect, setShowCannotDisconnect] = useState(false);
  const [errorOperatorName, setErrorOperatorName] = useState("");

  // A pending relationship the operator owns is an invite they sent, so this flow is a "withdraw", not a disconnect.
  const isWithdraw = pendingDisconnectRelationship?.status === "pending";

  const disconnectRelationship = () => {
    if (!pendingDisconnectRelationship) return;

    removeNetwork(pendingDisconnectRelationship.id)
      .unwrap()
      .then(() => {
        addToast(
          "success",
          isWithdraw
            ? `Withdrew invite to ${pendingDisconnectRelationship.operator.name}`
            : `Removed ${pendingDisconnectRelationship.operator.name} company`
        );
        setPendingDisconnectRelationship(undefined);
      })
      .catch((e) => {
        if (e.data?.error === "NETWORK-DISCONNECT-ACTIVE-JOBS") {
          setErrorOperatorName(pendingDisconnectRelationship.operator.name);
          setPendingDisconnectRelationship(undefined);
          setShowCannotDisconnect(true);
        } else {
          getErrorMessages(e).forEach((m) => addToast("danger", m));
        }
      });
  };

  return (
    <>
      <CannotDisconnectModal open={showCannotDisconnect} onClose={() => setShowCannotDisconnect(false)} operatorName={errorOperatorName} />
      <Modal
        open={!!pendingDisconnectRelationship}
        onClose={() => setPendingDisconnectRelationship(undefined)}
        className="relative w-[85vw] md:w-[514px] lg:w-[514px]"
      >
        {isLoading && <Loading />}
        <div className="max-w-lg sm:flex sm:items-start ">
          <div className="mt-3 text-center sm:mt-0 sm:text-left">
            <Typography variant="title">{isWithdraw ? "Withdraw Invite" : "Disconnect Relationship"}</Typography>
            <div className="mt-3">
              <Typography className="text-neutral-dark-gray">
                {isWithdraw
                  ? `Are you sure you want to withdraw your invite to ${pendingDisconnectRelationship?.operator.name || ""}?`
                  : `Are you sure you want to disconnect your relationship with ${pendingDisconnectRelationship?.operator.name || ""}?`}
              </Typography>
            </div>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-4">
          <Button variant="secondary" onClick={() => setPendingDisconnectRelationship(undefined)} className="px-8">
            Cancel
          </Button>
          <Button variant="primary" onClick={disconnectRelationship} className="!bg-danger px-8 hover:!bg-danger-dark">
            {isWithdraw ? "Withdraw" : "Disconnect"}
          </Button>
        </div>
      </Modal>
    </>
  );
};
